import type { NewPlan } from "./schemas/plan.js";
import type { NewProgram } from "./schemas/program.js";
import type { Plan } from "./schemas/plan.js";
import type { Program, ProgramSession } from "./schemas/program.js";
import type {
  ResolvedPlanTemplate,
  ResolvedProgramTemplate,
} from "./templates.js";
import { mapAuthoredText, type AuthoredText } from "./authoredText.js";
import type { Locale } from "./locale.js";

// Clone POST-body builders — the ONE place that knows how to turn an
// existing plan/program (a personal row OR a locale-resolved template)
// into a NewPlan / NewProgram payload. The id-stripping rules live here
// once so template-clone (TemplatesShelf, Library browser) and
// personal-clone can't drift:
//   - programs: session/slot ids are dropped — the server stamps fresh
//     UUIDs on POST so a clone never shares ids with its source (or
//     with another user's clone of the same template).
//   - plans: entries carry no ids (PlannedExerciseEntry is id-less),
//     so they pass through verbatim.
//   - a clone is a personal row with no assignees, and its status is the
//     caller's call: 'draft' by default (the Follow+Publish lifecycle
//     starts over for a copy of your own program, because you are making
//     a variant), 'active' when the copy IS the follow gesture — the
//     copy-on-follow of a Metria program (archive/programs-follow-model.md
//     §5 S5). A draft is neither followable nor startable, so a template
//     cloned as a draft costs three gestures instead of one.

/** The subset of a plan a clone copies — both a personal `Plan` and a
 *  locale-`ResolvedPlanTemplate` satisfy it structurally. */
export type PlanCloneSource = Pick<Plan, "name" | "entries"> & {
  notes?: string;
};

/** The subset of a program a clone copies — both a personal `Program`
 *  and a `ResolvedProgramTemplate` satisfy it structurally. */
export type ProgramCloneSource = Pick<
  Program,
  "name" | "cycleWeeks" | "sessionsPerWeek"
> & {
  // `AuthoredText | string`, not `string`: a program's description may
  // carry several languages (PE-21), while a resolved template's is
  // already flattened to one — both have to satisfy this.
  description?: AuthoredText | string;
  // Carried so a clone keeps the language its text was WRITTEN in;
  // without it the copy would fall back through "any available" and
  // could read in a language its author never chose.
  authorLocale?: Locale;
  // #336 S2: a clone keeps its rounds' types, as it keeps their sets.
  roundTypes?: Program["roundTypes"];
  sessions: ReadonlyArray<ProgramSession>;
};

// Compile-time proof the resolved template shapes stay assignable to the
// clone sources (a template field rename breaks here, not at runtime).
const _planTplAssignable: PlanCloneSource = null as unknown as Omit<
  ResolvedPlanTemplate,
  "id" | "description" | "difficulty" | "tags"
>;
const _programTplAssignable: ProgramCloneSource = null as unknown as Omit<
  ResolvedProgramTemplate,
  "id" | "description" | "difficulty" | "tags"
>;
void _planTplAssignable;
void _programTplAssignable;

/** Plan.name / Program.name cap (schema max 120). A rename suffix must
 *  never push the clone body over it. */
const NAME_MAX = 120;

// Caps EVERY language present, keeping the value's shape: a clone of a
// translated program must arrive with the same languages as the
// original, each within the schema ceiling.
const capName = <T extends AuthoredText | string>(name: T): T =>
  mapAuthoredText(name, (s) => (s.length > NAME_MAX ? s.slice(0, NAME_MAX) : s));

export const planCloneBody = (
  src: PlanCloneSource,
  // CO-17 — `authoredAs` is NEVER read off `src`: cloning someone else's
  // plan (a Metria template, or another user's row a future share makes
  // visible) must stamp the CLONER's own active view, not the source's.
  // The caller passes it explicitly; absent ⇒ the schema's own default.
  opts?: { name?: string; authoredAs?: Plan["authoredAs"] },
): NewPlan => ({
  name: capName(opts?.name ?? src.name),
  authoredAs: opts?.authoredAs ?? "athlete",
  entries: src.entries.map((e) => ({ ...e })),
  ...(src.notes ? { notes: src.notes } : {}),
});

export const programCloneBody = (
  src: ProgramCloneSource,
  // A clone of a translated program may be renamed in every language
  // it carries (see mapAuthoredText) — not collapsed to one.
  // CO-17 — same non-inheritance rule as planCloneBody above: `authoredAs`
  // comes from the caller (the cloner's own active view), never from `src`.
  opts?: {
    name?: AuthoredText | string;
    status?: Program["status"];
    authoredAs?: Program["authoredAs"];
  },
): NewProgram => ({
  name: capName(opts?.name ?? src.name),
  authoredAs: opts?.authoredAs ?? "athlete",
  ...(src.description ? { description: src.description } : {}),
  ...(src.authorLocale ? { authorLocale: src.authorLocale } : {}),
  cycleWeeks: src.cycleWeeks,
  sessionsPerWeek: src.sessionsPerWeek,
  ...(src.roundTypes ? { roundTypes: [...src.roundTypes] } : {}),
  sessions: src.sessions.map((s) => ({
    name: s.name,
    ...(s.notes ? { notes: s.notes } : {}),
    slots: s.slots.map((slot) => {
      const { id: _slotId, ...rest } = slot;
      void _slotId;
      return rest;
    }),
  })),
  status: opts?.status ?? "draft",
});
